import React, { useEffect, useState } from 'react';
import { apiDelete, apiGet, apiPut } from '@/context/AuthContext';
import { EyeOff, Image, Loader2, RefreshCw, ShieldCheck, Trash2, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { API_BASE_URL } from '@/lib/api';

const API = API_BASE_URL;

export default function AdminImages() {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [moderatingId, setModeratingId] = useState(null);
  const [filter, setFilter] = useState('all');
  const { toast } = useToast();

  const load = () => {
    setLoading(true);
    apiGet('/api/uploads').then(r => setUploads(r.data.uploads || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (upload) => {
    if (!window.confirm('Slet dette uploadede billede?')) return;
    setDeletingId(upload.id);
    try {
      await apiDelete(`/api/uploads/${upload.id}`);
      setUploads(current => current.filter(item => item.id !== upload.id));
      toast({
        title: 'Billede slettet',
        description: 'Billedet blev fjernet fra billedoversigten og diasshowet.',
      });
    } catch (err) {
      toast({
        title: 'Sletning mislykkedes',
        description: err.response?.data?.detail || 'Prøv igen om lidt.',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const moderateUpload = async (upload, action) => {
    setModeratingId(`${upload.id}-${action}`);
    try {
      const res = await apiPut(`/api/uploads/${upload.id}/moderation`, { action });
      setUploads(current => current.map(item => item.id === upload.id ? res.data.upload : item));
      toast({
        title: action === 'approve' ? 'Billede godkendt' : 'Billede skjult',
        description: action === 'approve'
          ? 'Billedet kan nu vises i diasshowet og live-optællingen.'
          : 'Billedet er skjult fra diasshowet og live-optællingen.',
      });
    } catch (err) {
      toast({
        title: 'Moderation mislykkedes',
        description: err.response?.data?.detail || 'Prøv igen om lidt.',
        variant: 'destructive',
      });
    } finally {
      setModeratingId(null);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatus = (upload) => {
    if (upload.category !== 'event') return 'profile';
    if (upload.hidden) return 'hidden';
    if (upload.approved) return 'approved';
    return 'pending';
  };

  const filteredUploads = uploads.filter((upload) => {
    const status = getStatus(upload);
    return filter === 'all' || status === filter;
  });

  const counts = uploads.reduce((acc, upload) => {
    const status = getStatus(upload);
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="animate-fade-in" data-testid="admin-images-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>Billeder</h1>
          <p className="text-sm text-slate-400 mt-1">{filteredUploads.length} af {uploads.length} filer vises</p>
        </div>
        <Button variant="outline" onClick={load} className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700" data-testid="refresh-images-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="image-moderation-filters">
        {[
          { key: 'all', label: 'Alle', count: uploads.length },
          { key: 'pending', label: 'Afventer', count: counts.pending || 0 },
          { key: 'approved', label: 'Godkendt', count: counts.approved || 0 },
          { key: 'hidden', label: 'Skjult', count: counts.hidden || 0 },
        ].map(item => (
          <button
            key={item.key}
            type="button"
            onClick={() => setFilter(item.key)}
            className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
              filter === item.key
                ? 'bg-blue-500/15 border-blue-500/40 text-blue-300'
                : 'bg-slate-900/40 border-slate-700/60 text-slate-400 hover:text-slate-200 hover:border-slate-600'
            }`}
            data-testid={`filter-${item.key}`}
          >
            {item.label} <span className="text-slate-500">{item.count}</span>
          </button>
        ))}
      </div>

      {loading ? <p className="text-slate-400">Indlæser...</p> : filteredUploads.length === 0 ? (
        <div className="text-center py-12">
          <Image className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Ingen billeder endnu</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filteredUploads.map(u => (
            <div key={u.id} className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden" data-testid={`upload-${u.id}`}>
              <div className="aspect-square bg-slate-900/50 flex items-center justify-center">
                <ImagePreview storagePath={u.storage_path} />
              </div>
              <div className="p-3">
                <StatusBadge status={getStatus(u)} />
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs text-slate-300 truncate">{u.guest_name || 'Ukendt'}</p>
                    <p className="text-xs text-slate-500 truncate">{u.original_filename}</p>
                    <p className="text-xs text-slate-600">{formatSize(u.size)}</p>
                    {u.approved_at && <p className="text-xs text-slate-600 truncate">Godkendt af {u.approved_by || 'admin'}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDelete(u)}
                    disabled={deletingId === u.id}
                    className="p-1.5 rounded-md text-slate-500 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-50"
                    aria-label={`Slet ${u.original_filename || 'billede'}`}
                    data-testid={`delete-upload-${u.id}`}
                  >
                    {deletingId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
                {u.category === 'event' && (
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => moderateUpload(u, 'approve')}
                      disabled={moderatingId === `${u.id}-approve` || (u.approved && !u.hidden)}
                      className="h-8 bg-emerald-500/10 border-emerald-500/20 text-emerald-300 hover:bg-emerald-500/20"
                      data-testid={`approve-upload-${u.id}`}
                    >
                      {moderatingId === `${u.id}-approve` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                      Godkend
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => moderateUpload(u, 'hide')}
                      disabled={moderatingId === `${u.id}-hide` || u.hidden}
                      className="h-8 bg-slate-900/50 border-slate-700 text-slate-300 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/30"
                      data-testid={`hide-upload-${u.id}`}
                    >
                      {moderatingId === `${u.id}-hide` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <EyeOff className="w-3.5 h-3.5" />}
                      Skjul
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    profile: 'bg-slate-500/10 text-slate-400 border-slate-600/40',
    pending: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
    approved: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
    hidden: 'bg-red-500/10 text-red-300 border-red-500/30',
  };
  const labels = {
    profile: 'Profil',
    pending: 'Afventer',
    approved: 'Godkendt',
    hidden: 'Skjult',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-medium mb-2 ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

function ImagePreview({ storagePath }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!storagePath) return;
    const token = localStorage.getItem('token');
    fetch(`${API}/api/files/${storagePath}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.ok) return r.blob(); throw new Error(); })
      .then(blob => setSrc(URL.createObjectURL(blob)))
      .catch(() => {});
    return () => { if (src) URL.revokeObjectURL(src); };
  }, [storagePath]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!src) return <User className="w-8 h-8 text-slate-600" />;
  return <img src={src} alt="" className="w-full h-full object-cover" />;
}
